import { AxiosInstance, AxiosRequestConfig } from "axios";

import { AxiosInstanceInterceptorRequestExtensionI } from "@/api/interceptors/interface";

/**
 * Roles in the user app (BACKLOG C-14, GLE-8382). Admin (global), LGA (per
 * workspace) and Pod Lead (per pod) are grants on the user account; a caller
 * holds none. Every route is behind the backend switch `user_roles_enabled`:
 * while it is off no route is registered and each one answers 404 with
 * error_code 1035, which the app reads as "roles are not switched on".
 *
 * The server checks every request; the app only hides what a user can't use.
 * Shapes are the contract frozen in the C-14 coordination note.
 */

export const MANAGE_ROLES = {
  ADMIN: "admin",
  LGA: "lga",
  POD_LEAD: "pod_lead",
} as const;

export type ManageRoleI = (typeof MANAGE_ROLES)[keyof typeof MANAGE_ROLES];

export interface ManageMeRoleI {
  grant_id: string;
  role: ManageRoleI;
  workspace_id?: string | null;
  workspace_name?: string | null;
  pod_id?: string | null;
  pod_name?: string | null;
}

export interface ManageMeWorkspaceI {
  id: string;
  name: string;
  /** "write" for an LGA (or an Admin), "pod_read" for a Pod Lead. */
  access: "write" | "pod_read";
}

export interface ManageMePodI {
  id: string;
  name: string;
  workspace_id: string;
}

/**
 * The phase-2 Team pages, one key per page's backend switch (C-14 phase 2):
 * a nav item with `feature` shows only while its key is true.
 */
export const MANAGE_FEATURES = {
  CAMPAIGNS: "campaigns",
  ANALYTICS: "analytics",
  REPS: "reps",
  INSIGHTS: "insights",
  INCENTIVES: "incentives",
  // C-16 S3: Team → Pipeline (user_roles_pipeline_enabled).
  PIPELINE: "pipeline",
  // C-16 S3: Team → Forecast (user_roles_forecast_enabled).
  FORECAST: "forecast",
  // C-16 S2: Team → EOM (user_roles_eom_enabled).
  EOM: "eom",
  // C-16 S8: Campaign Health's goals, triage log and launch checklist
  // (user_roles_campaign_ops_enabled).
  CAMPAIGN_OPS: "campaign_ops",
  // C-16 S8: Team → Health → Lead ingestion (user_roles_lead_ingestion_enabled).
  LEAD_INGESTION: "lead_ingestion",
} as const;

export type ManageFeatureI =
  (typeof MANAGE_FEATURES)[keyof typeof MANAGE_FEATURES];

export type ManageFeaturesI = Record<ManageFeatureI, boolean> & {
  /**
   * C-16 S8 B-1: an Admin's "All workspaces" on Health and Admin → Pods
   * (user_roles_all_workspaces_enabled). Absent on an older backend.
   */
  all_workspaces_health?: boolean;
};

/** 200 with empty lists for a plain caller. */
export interface ManageMeI {
  roles: Array<ManageMeRoleI>;
  is_admin: boolean;
  workspaces: Array<ManageMeWorkspaceI>;
  pods: Array<ManageMePodI>;
  /** The request was signed with the impersonation key: writes answer 403. */
  impersonating: boolean;
  /** Which phase-2 Team pages are switched on; all false when absent. */
  features: ManageFeaturesI;
}

export interface ManageUserI {
  id: string;
  name: string;
  email: string;
  workspace_id?: string | null;
  workspace_name?: string | null;
}

export interface ManageGrantI {
  id: string;
  user_id: string;
  user_name: string;
  user_email: string;
  role: ManageRoleI;
  workspace_id?: string | null;
  workspace_name?: string | null;
  pod_id?: string | null;
  pod_name?: string | null;
  granted_by_user_id?: string | null;
  granted_by_name?: string | null;
  granted_at: string;
  revoked_by_user_id?: string | null;
  revoked_by_name?: string | null;
  revoked_at?: string | null;
  note?: string | null;
  source: "app" | "seed" | string;
}

export interface ManageWorkspaceI {
  id: string;
  name: string;
}

export interface ManagePodMemberI {
  user_id: string;
  name: string;
  email: string;
  added_at: string;
  /** The member left the pod's workspace roster; reads leave them out. */
  moved: boolean;
}

export interface ManagePodI {
  id: string;
  name: string;
  workspace_id: string;
  archived_at?: string | null;
  lead?: { grant_id: string; user_id: string; name: string } | null;
  members: Array<ManagePodMemberI>;
  // Only on the Admin's "All workspaces" read (C-16 B-1, GET
  // /user/v1/manage/pods): the pod's workspace.
  workspace_name?: string;
}

export interface GrantRoleRequestI {
  user_id: string;
  role: ManageRoleI;
  workspace_id?: string;
  pod_id?: string;
  note?: string;
}

export interface UpdatePodRequestI {
  name?: string;
  archived?: true;
}

/**
 * A 404 on manage/me means "switched off": an answer, not an error to log. A
 * fresh object per request, because the meta interceptor writes into `meta`.
 */
const switchProbeConfig = (): AxiosRequestConfig &
  AxiosInstanceInterceptorRequestExtensionI => ({
  meta: { expectedStatuses: [404] },
});

export const ManageAPIEndpoints = (api: AxiosInstance) => ({
  getMe: () => api.get<ManageMeI>("/v1/manage/me", switchProbeConfig()),

  /**
   * `q` needs 2+ characters; at most 20 users. Without `workspaceId` it is
   * Admin only, over every user; with it, Admin or that workspace's LGA, and
   * only the workspace's live roster.
   */
  searchUsers: (q: string, workspaceId?: string) =>
    api.get<{ users: Array<ManageUserI> }>("/v1/manage/users", {
      params: workspaceId ? { q, workspace_id: workspaceId } : { q },
    }),

  /**
   * Live and revoked grants, newest first. Neither parameter (Admins, C-16
   * B-4): every live grant; an older backend answers 401.
   */
  listGrants: (params: { user_id?: string; workspace_id?: string }) =>
    api.get<{ grants: Array<ManageGrantI> }>("/v1/manage/roles", { params }),

  grantRole: (body: GrantRoleRequestI) =>
    api.post<{ grant: ManageGrantI }>("/v1/manage/roles", body),

  revokeGrant: (grantId: string) =>
    api.delete<{ grant: ManageGrantI }>(
      `/v1/manage/roles/${encodeURIComponent(grantId)}`
    ),

  listWorkspaces: () =>
    api.get<{ workspaces: Array<ManageWorkspaceI> }>("/v1/manage/workspaces"),

  listPods: (workspaceId: string) =>
    api.get<{ pods: Array<ManagePodI> }>(
      `/v1/manage/workspace/${encodeURIComponent(workspaceId)}/pods`
    ),

  /** Admins: every workspace's pods, each with its workspace_id (C-16 B-1). */
  listAllPods: () => api.get<{ pods: Array<ManagePodI> }>("/v1/manage/pods"),

  createPod: (workspaceId: string, name: string) =>
    api.post<{ pod: ManagePodI }>(
      `/v1/manage/workspace/${encodeURIComponent(workspaceId)}/pods`,
      { name }
    ),

  updatePod: (podId: string, body: UpdatePodRequestI) =>
    api.patch<{ pod: ManagePodI }>(
      `/v1/manage/pods/${encodeURIComponent(podId)}`,
      body
    ),

  addPodMember: (podId: string, userId: string) =>
    api.post(`/v1/manage/pods/${encodeURIComponent(podId)}/members`, {
      user_id: userId,
    }),

  removePodMember: (podId: string, userId: string) =>
    api.delete(
      `/v1/manage/pods/${encodeURIComponent(
        podId
      )}/members/${encodeURIComponent(userId)}`
    ),

  /** Sets or clears (null) the pod's Pod Lead grant (coordinator D2). */
  setPodLead: (podId: string, userId: string | null) =>
    api.put(`/v1/manage/pods/${encodeURIComponent(podId)}/lead`, {
      user_id: userId,
    }),
});
